import React from 'react';
import { generationScenarioInputMatches } from './layoutPublishUi';
import type { GenerationImpactScenarioInput, GenerationImpactScenarioSnapshot } from '../../../types/generationImpact';

interface Props {
  input: GenerationImpactScenarioInput;
  inputIsSaved: boolean;
  selectedScenarioId: string | null;
  projectScenario: GenerationImpactScenarioSnapshot | null;
  projectScenarioExpected: boolean;
  projectRevisionMatches: boolean;
  publishedVersion: number | null;
  busy: boolean;
  onOpenProjectScenario: () => void;
}

const mw = (value: number): string => value.toLocaleString(undefined, { maximumFractionDigits: 3 });
const demand = (value: number): string => Number.isFinite(value) && value > 0 ? `${mw(value)} MW facility demand` : 'Facility demand not set';

/** View-only comparison. The host owns loading and draft confirmation; this
 * strip never changes, solves, promotes, or publishes a scenario. */
export function GenerationScenarioBasis({ input, inputIsSaved, selectedScenarioId, projectScenario,
  projectScenarioExpected, projectRevisionMatches, publishedVersion, busy, onOpenProjectScenario }: Props): React.JSX.Element {
  const projectOpen = projectScenario != null && selectedScenarioId === projectScenario.id && inputIsSaved
    && projectRevisionMatches && generationScenarioInputMatches(input, projectScenario);
  const gridCount = input.units.filter(unit => unit.technology === 'grid-import').reduce((total, unit) => total + unit.count, 0);
  const onsiteCount = input.units.filter(unit => unit.technology !== 'grid-import').reduce((total, unit) => total + unit.count, 0);
  const equipmentLabel = [gridCount > 0 ? `${gridCount} declared grid feed${gridCount === 1 ? '' : 's'}` : '',
    onsiteCount > 0 ? `${onsiteCount} modeled on-site unit${onsiteCount === 1 ? '' : 's'}` : ''].filter(Boolean).join('; ');
  return <section aria-label="Scenario basis" className="grid min-w-0 overflow-hidden rounded-lg border border-border bg-surface-1 md:grid-cols-2" data-testid="generation-scenario-basis">
    <div className="min-w-0 px-4 py-3" data-testid="generation-working-basis">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-text-primary">{inputIsSaved ? 'Open saved scenario' : 'Working draft'}</h2>
        <span className="text-xs text-text-muted">{inputIsSaved ? 'Saved inputs' : 'Unsaved inputs'}</span>
      </div>
      <p className="mt-1 truncate text-sm text-text-primary">{input.name || 'Unnamed scenario'}</p>
      <p className="mt-1 text-sm font-medium tabular-nums text-text-primary">{demand(input.facilityLoadMW)}</p>
      <p className="mt-1 text-xs text-text-secondary">{equipmentLabel || 'No equipment selected'}</p>
      <p className="mt-2 max-w-prose text-xs leading-relaxed text-text-muted">{projectOpen
        ? 'Results below use the project scenario. Changes stay in the editor until saved.'
        : 'Results below use these inputs. The project basis remains separate.'}</p>
    </div>
    <div className="min-w-0 border-t border-border bg-surface-2 px-4 py-3 md:border-l md:border-t-0" data-testid="generation-project-basis">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-text-primary">{publishedVersion != null ? 'Published project basis' : 'Project scenario'}</h2>
        {publishedVersion != null && <span className="text-xs text-text-secondary">Layout v{publishedVersion}</span>}
      </div>
      {projectScenario ? <>
        <p className="mt-1 truncate text-sm text-text-primary">{projectScenario.name}</p>
        <p className="mt-1 text-sm font-medium tabular-nums text-text-primary">{demand(projectScenario.input.facilityLoadMW)}</p>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
          <button type="button" disabled={busy || projectOpen} onClick={onOpenProjectScenario}
            className="min-h-9 rounded-md border border-primary/60 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="generation-open-project-scenario">{projectOpen ? 'Project scenario open' : 'Open project scenario'}</button>
          <span className="text-xs text-text-muted">{projectOpen ? 'This is the saved project basis.' : 'Unsaved changes require confirmation.'}</span>
        </div>
      </> : <p className="mt-2 max-w-prose text-xs leading-relaxed text-text-secondary">{projectScenarioExpected
        ? 'The selected project scenario is unavailable or its input basis changed. Review saved scenarios before using it.'
        : 'No project scenario selected. Save a scenario, then select it for the project in Publish & handoff.'}</p>}
    </div>
  </section>;
}
